import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { FiPlus, FiTrash2, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { loadTable, getTableData } from '../../db-viewer/data/dataAccess';
import { addNotification } from '../../../lib/notifications';
import SearchableDropdown from '../../../components/ui/SearchableDropdown';
import { setFormDirty, registerSaveHandler } from '../../../lib/unsavedChanges';
import {
  postJournalEntry, voidJournalEntry, validateBalanced,
  JournalLineInput, CoaLite,
} from '../utils/glPosting';
import {
  OwnerEntity, getCurrentUser, getAccountOwner, getAccountOwnerForUser,
  getOwnerEntities, getClientEntities,
} from '../utils/ownerEntities';
import { getProfitCenters } from '../utils/profitCenters';
import './JournalEntriesTab.css';

// =============================================
// Make Journal Entries — manual double-entry bookkeeping (Your / Client's Accounts)
// =============================================
// Bookkeeper-facing tab to hand-enter balanced journal entries against the Chart
// of Accounts. Hosted in two places (shared component): the standalone Transactions
// → "Make Journal Entries" sub-page, AND a right-pane tab in Process Docs/Receipts.
// Writes acctg_gl_journal + acctg_gl_journal_line and moves acctg_coa balances via
// the shared glPosting helper. NEVER mounted under DMS (bookkeeping only).
// =============================================

interface JournalEntriesTabProps {
  viewMode?: 'owner' | 'clients';
  selectedClientId?: number | string | null;
  documentId?: number | null;       // statement doc, when hosted inside Process Receipts
  defaultOwnerKey?: string | null;   // "owner_type:owner_id" to preselect the entity
}

interface CoaRow extends CoaLite { account_owner_id: number; owner_type: string; owner_id: number; account_number: string | null; account_name: string; is_header: number; is_active?: number; is_deleted?: number; }
interface JournalRow { journal_id: number; account_owner_id: number; owner_type: string; owner_id: number; journal_date: string; journal_number: string | null; memo: string | null; source: string; status: string; total_debit: number; total_credit: number; is_deleted?: number; [k: string]: any; }
interface JournalLineRow { line_id: number; journal_id: number; coa_id: number; debit: number; credit: number; line_memo: string | null; is_deleted?: number; [k: string]: any; }

interface DraftLine { coa_id: number | null; debit: string; credit: string; pc: string; }

const NOW_DATE = () => new Date().toISOString().slice(0, 10);
const num = (v: any) => Number(v) || 0;
const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

// A new line inherits the previous line's profit center to cut re-picking.
const blankLine = (pc = ''): DraftLine => ({ coa_id: null, debit: '', credit: '', pc });

const JournalEntriesTab: React.FC<JournalEntriesTabProps> = ({ viewMode = 'owner', selectedClientId = null, documentId = null, defaultOwnerKey = null }) => {
  const [dataLoaded, setDataLoaded] = useState(false);
  const [usersData, setUsersData] = useState<Record<string, any>[]>([]);
  const [businessesData, setBusinessesData] = useState<Record<string, any>[]>([]);
  const [coaAccounts, setCoaAccounts] = useState<CoaRow[]>([]);
  const [journals, setJournals] = useState<JournalRow[]>([]);
  const [journalLines, setJournalLines] = useState<JournalLineRow[]>([]);
  const [locationsData, setLocationsData] = useState<Record<string, any>[]>([]);
  const [unitsData, setUnitsData] = useState<Record<string, any>[]>([]);

  const [ownerKey, setOwnerKey] = useState<string>(defaultOwnerKey || '');
  const [entryDate, setEntryDate] = useState<string>(NOW_DATE());
  const [memo, setMemo] = useState<string>('');
  const [lines, setLines] = useState<DraftLine[]>([blankLine(), blankLine()]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);

  // ---- Load data ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [u, b, coa, j, jl, loc, un] = await Promise.all([
          loadTable('users'), loadTable('user_businesses'), loadTable('acctg_coa'),
          loadTable('acctg_gl_journal'), loadTable('acctg_gl_journal_line'),
          loadTable('user_business_location'), loadTable('user_business_unit'),
        ]);
        if (cancelled) return;
        setUsersData(u); setBusinessesData(b); setCoaAccounts(coa as CoaRow[]);
        setJournals(j as JournalRow[]); setJournalLines(jl as JournalLineRow[]);
        setLocationsData(loc); setUnitsData(un);
        setDataLoaded(true);
      } catch {
        if (cancelled) return;
        setUsersData(getTableData('users')); setBusinessesData(getTableData('user_businesses'));
        setCoaAccounts(getTableData('acctg_coa') as CoaRow[]);
        setJournals(getTableData('acctg_gl_journal') as JournalRow[]);
        setJournalLines(getTableData('acctg_gl_journal_line') as JournalLineRow[]);
        setLocationsData(getTableData('user_business_location'));
        setUnitsData(getTableData('user_business_unit'));
        setDataLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const currentUser = useMemo(() => dataLoaded ? getCurrentUser(usersData) : null, [usersData, dataLoaded]);
  const accountOwner = useMemo(() => {
    if (!currentUser) return null;
    return getAccountOwnerForUser(currentUser.user_id, usersData); // logged-in user's own account (multi-tenant safe)
  }, [currentUser, usersData]);
  const accountOwnerId = accountOwner ? Number(accountOwner.user_id) : 0;

  const ownerEntities = useMemo(() => {
    if (!dataLoaded || !accountOwnerId) return [] as OwnerEntity[];
    if (viewMode === 'clients') {
      if (selectedClientId == null) return [];
      return getClientEntities(selectedClientId, usersData, businessesData);
    }
    return getOwnerEntities(accountOwnerId, usersData, businessesData);
  }, [dataLoaded, accountOwnerId, viewMode, selectedClientId, usersData, businessesData]);

  // Default the entity selection once entities resolve.
  useEffect(() => {
    if (!ownerKey && ownerEntities.length) {
      const def = defaultOwnerKey && ownerEntities.some(e => `${e.owner_type}:${e.owner_id}` === defaultOwnerKey)
        ? defaultOwnerKey : `${ownerEntities[0].owner_type}:${ownerEntities[0].owner_id}`;
      setOwnerKey(def);
    }
  }, [ownerEntities, ownerKey, defaultOwnerKey]);

  const selectedEntity = useMemo(() => ownerEntities.find(e => `${e.owner_type}:${e.owner_id}` === ownerKey) || null, [ownerEntities, ownerKey]);

  // Profit centers (locations/units flagged "tracks own profitability") for the
  // selected business entity. Empty for non-business entities → per-line picker hidden.
  const profitCenters = useMemo(
    () => getProfitCenters(selectedEntity && selectedEntity.owner_type === 'business' ? selectedEntity.owner_id : null, locationsData, unitsData),
    [selectedEntity, locationsData, unitsData]);

  // COA options scoped to the chosen entity (detail accounts only; fall back to all of the owner's).
  const coaOptions = useMemo(() => {
    const accts = coaAccounts.filter(a => a.is_deleted !== 1 && a.is_active !== 0 && Number(a.is_header) !== 1 &&
      // eslint-disable-next-line eqeqeq
      a.account_owner_id == accountOwnerId);
    const scoped = selectedEntity
      // eslint-disable-next-line eqeqeq
      ? accts.filter(a => a.owner_type === selectedEntity.owner_type && a.owner_id == selectedEntity.owner_id)
      : [];
    const list = scoped.length ? scoped : accts;
    return list.slice().sort((a, b) => (a.account_number || '').localeCompare(b.account_number || ''))
      .map(a => ({ value: String(a.coa_id), label: `${a.account_number ? a.account_number + ' ' : ''}${a.account_name}` }));
  }, [coaAccounts, selectedEntity, accountOwnerId]);

  const coaName = useCallback((coaId: number) => {
    const a = coaAccounts.find(x => Number(x.coa_id) === Number(coaId));
    return a ? `${a.account_number ? a.account_number + ' ' : ''}${a.account_name}` : `Account #${coaId}`;
  }, [coaAccounts]);

  // Journal entries for the selected entity.
  const entityJournals = useMemo(() => {
    if (!selectedEntity) return [];
    return journals
      .filter(j => j.is_deleted !== 1 && j.owner_type === selectedEntity.owner_type &&
        // eslint-disable-next-line eqeqeq
        j.owner_id == selectedEntity.owner_id && j.account_owner_id == accountOwnerId)
      .slice()
      .sort((a, b) => (b.journal_date || '').localeCompare(a.journal_date || '') || Number(b.journal_id) - Number(a.journal_id));
  }, [journals, selectedEntity, accountOwnerId]);

  // ---- Form math ----
  const totals = useMemo(() => {
    const debit = round2(lines.reduce((s, l) => s + num(l.debit), 0));
    const credit = round2(lines.reduce((s, l) => s + num(l.credit), 0));
    return { debit, credit, diff: round2(debit - credit) };
  }, [lines]);

  const toLineInputs = useCallback((): JournalLineInput[] =>
    lines.filter(l => l.coa_id != null && (num(l.debit) > 0 || num(l.credit) > 0))
      .map(l => {
        const { location_id, unit_id } = profitCenters.decode(l.pc);
        return { coa_id: Number(l.coa_id), debit: num(l.debit), credit: num(l.credit), location_id, unit_id };
      }), [lines, profitCenters]);

  const validationError = useMemo(() => validateBalanced(toLineInputs()), [toLineInputs]);
  const hasInput = useMemo(() => memo.trim() !== '' || lines.some(l => l.coa_id != null || l.debit !== '' || l.credit !== ''), [memo, lines]);

  const markDirty = useCallback(() => setFormDirty(true), []);

  const updateLine = useCallback((idx: number, patch: Partial<DraftLine>) => {
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));
    markDirty();
  }, [markDirty]);
  const addLine = useCallback(() => { setLines(prev => [...prev, blankLine(prev.length ? prev[prev.length - 1].pc : '')]); markDirty(); }, [markDirty]);
  const removeLine = useCallback((idx: number) => { setLines(prev => prev.length <= 2 ? prev : prev.filter((_, i) => i !== idx)); markDirty(); }, [markDirty]);

  const resetForm = useCallback(() => {
    setEntryDate(NOW_DATE()); setMemo(''); setLines([blankLine(), blankLine()]);
    setFormDirty(false);
  }, []);

  // ---- Save (also the registered unsaved-changes handler) ----
  const saveEntry = useCallback(async (): Promise<boolean> => {
    if (!hasInput) { setFormDirty(false); return true; }   // nothing started → nothing to save
    if (!selectedEntity) { setMsg('Pick an entity first.'); return false; }
    const lineInputs = toLineInputs();
    const err = validateBalanced(lineInputs);
    if (err) { setMsg(err); return false; }
    setBusy(true);
    try {
      const result = await postJournalEntry({
        accountOwnerId,
        ownerType: selectedEntity.owner_type,
        ownerId: selectedEntity.owner_id,
        date: entryDate,
        memo,
        source: 'manual',
        documentId,
        currency: 'CAD',
        createdBy: currentUser ? currentUser.user_id : null,
        lines: lineInputs,
        coaAccounts,
      });
      // Reflect into local state
      setJournals(prev => [result.journal as JournalRow, ...prev]);
      setJournalLines(prev => [...(result.lines as JournalLineRow[]), ...prev]);
      setCoaAccounts(prev => prev.map(a => {
        const u = result.balanceUpdates.find(x => x.coa_id === Number(a.coa_id));
        return u ? { ...a, current_balance: u.current_balance } : a;
      }));
      if (currentUser && currentUser.user_id !== accountOwnerId) {
        addNotification({
          account_owner_id: accountOwnerId, type: 'journal_entry_added', title: 'Journal Entry Added',
          message: `${currentUser.first_name} added journal entry ${result.journal.journal_number || ''}`.trim(),
          actor_user_id: currentUser.user_id, actor_name: `${currentUser.first_name} ${currentUser.last_name}`.trim(),
        });
      }
      resetForm();
      setMsg(`Posted ${result.journal.journal_number}.`);
      return true;
    } catch (e: any) {
      setMsg(e?.message ? `Couldn't post: ${e.message}` : 'Could not post the journal entry. Please try again.');
      return false;
    } finally {
      setBusy(false);
    }
  }, [hasInput, selectedEntity, toLineInputs, accountOwnerId, entryDate, memo, documentId, currentUser, coaAccounts, resetForm]);

  // Register the save handler for the unsaved-changes modal while mounted.
  useEffect(() => {
    registerSaveHandler(saveEntry);
    return () => registerSaveHandler(null);
  }, [saveEntry]);

  // ---- Void ----
  const handleVoid = useCallback(async (journal: JournalRow) => {
    const jLines = journalLines.filter(l => Number(l.journal_id) === Number(journal.journal_id) && l.is_deleted !== 1)
      .map(l => ({ coa_id: Number(l.coa_id), debit: num(l.debit), credit: num(l.credit) }));
    setBusy(true);
    try {
      const { balanceUpdates } = await voidJournalEntry(journal as any, jLines, coaAccounts);
      setJournals(prev => prev.map(j => j.journal_id === journal.journal_id ? { ...j, status: 'void' } : j));
      setCoaAccounts(prev => prev.map(a => {
        const u = balanceUpdates.find(x => x.coa_id === Number(a.coa_id));
        return u ? { ...a, current_balance: u.current_balance } : a;
      }));
      setMsg(`Voided ${journal.journal_number || `#${journal.journal_id}`}.`);
    } catch (e: any) {
      setMsg(e?.message ? `Couldn't void: ${e.message}` : 'Could not void the entry. Please try again.');
    } finally { setBusy(false); }
  }, [journalLines, coaAccounts]);

  if (!dataLoaded) return <div className="je-tab"><div className="je-empty">Loading…</div></div>;

  const noEntity = ownerEntities.length === 0;
  const canSave = !busy && hasInput && !validationError;

  return (
    <div className="je-tab">
      <div className="je-head">
        <h3 className="je-title">Make Journal Entries</h3>
        <p className="je-sub">Record a balanced double-entry transaction (debits must equal credits). Posting updates account balances.</p>
      </div>

      {noEntity ? (
        <div className="je-empty">
          {viewMode === 'clients' && selectedClientId == null
            ? 'Pick a client first.'
            : 'No accounts available — set up your Chart of Accounts first.'}
        </div>
      ) : (
        <>
          {/* Entity picker */}
          <div className="je-field">
            <label className="je-label">Entity (whose books)</label>
            <SearchableDropdown
              className="je-owner-select"
              options={ownerEntities.map(e => ({ value: `${e.owner_type}:${e.owner_id}`, label: `${e.display_name} (${e.badge})` }))}
              value={ownerKey}
              onChange={(v: string) => { setOwnerKey(v); setExpanded(null); setLines(prev => prev.map(l => ({ ...l, pc: '' }))); }}
              placeholder="Choose an entity"
            />
          </div>

          {/* New entry form */}
          <div className="je-card">
            <div className="je-form-row">
              <div className="je-field je-field-date">
                <label className="je-label">Date</label>
                <input type="date" className="je-input" value={entryDate} onChange={e => { setEntryDate(e.target.value); markDirty(); }} />
              </div>
              <div className="je-field je-field-memo">
                <label className="je-label">Memo</label>
                <input type="text" className="je-input" value={memo} placeholder="Description of the entry" onChange={e => { setMemo(e.target.value); markDirty(); }} />
              </div>
            </div>

            <div className="je-lines">
              <div className="je-lines-header">
                <span className="je-col-acct">Account</span>
                <span className="je-col-amt">Debit</span>
                <span className="je-col-amt">Credit</span>
                <span className="je-col-x" />
              </div>
              {lines.map((l, idx) => (
                <div className="je-line-wrap" key={idx}>
                  <div className="je-line">
                    <div className="je-col-acct">
                      <SearchableDropdown
                        className="je-line-acct"
                        options={coaOptions}
                        value={l.coa_id != null ? String(l.coa_id) : ''}
                        onChange={(v: string) => updateLine(idx, { coa_id: v ? Number(v) : null })}
                        placeholder={coaOptions.length ? 'Select account…' : 'No accounts — add some in Chart of Accounts'}
                      />
                    </div>
                    <input
                      className="je-col-amt je-amt" type="number" min="0" step="0.01" inputMode="decimal"
                      value={l.debit}
                      onChange={e => updateLine(idx, { debit: e.target.value, credit: e.target.value ? '' : l.credit })}
                      placeholder="0.00"
                    />
                    <input
                      className="je-col-amt je-amt" type="number" min="0" step="0.01" inputMode="decimal"
                      value={l.credit}
                      onChange={e => updateLine(idx, { credit: e.target.value, debit: e.target.value ? '' : l.debit })}
                      placeholder="0.00"
                    />
                    <button className="je-col-x je-line-remove" type="button" onClick={() => removeLine(idx)} disabled={lines.length <= 2} title="Remove line">
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                  {profitCenters.hasAny && (
                    <div className="je-line-pc">
                      <span className="je-line-pc-label">Profit center</span>
                      <SearchableDropdown
                        className="je-line-pc-select"
                        options={[{ value: '', label: '— None —' }, ...profitCenters.options]}
                        value={l.pc}
                        onChange={(v: string) => updateLine(idx, { pc: v })}
                        placeholder="— None —"
                      />
                    </div>
                  )}
                </div>
              ))}
              <button className="je-add-line" type="button" onClick={addLine}><FiPlus size={14} /> Add line</button>
            </div>

            <div className="je-totals">
              <span>Debits <strong>{totals.debit.toFixed(2)}</strong></span>
              <span>Credits <strong>{totals.credit.toFixed(2)}</strong></span>
              <span className={totals.diff === 0 ? 'je-bal-ok' : 'je-bal-off'}>
                {totals.diff === 0 ? 'Balanced' : `Out by ${Math.abs(totals.diff).toFixed(2)}`}
              </span>
            </div>

            {validationError && hasInput && <div className="je-hint">{validationError}</div>}
            {msg && <div className="je-msg">{msg}</div>}

            <div className="je-actions">
              <button className="je-btn je-btn-secondary" type="button" onClick={resetForm} disabled={busy || !hasInput}>Clear</button>
              <button className="je-btn je-btn-primary" type="button" onClick={saveEntry} disabled={!canSave}>
                {busy ? 'Posting…' : 'Post Entry'}
              </button>
            </div>
          </div>

          {/* Existing entries */}
          <div className="je-list">
            <h4 className="je-list-title">Journal entries {selectedEntity ? `· ${selectedEntity.display_name}` : ''} <span className="je-count">{entityJournals.length}</span></h4>
            {entityJournals.length === 0 ? (
              <div className="je-empty je-empty-sm">No journal entries yet for this entity.</div>
            ) : entityJournals.map(j => {
              const jLines = journalLines.filter(l => Number(l.journal_id) === Number(j.journal_id) && l.is_deleted !== 1);
              const isOpen = expanded === j.journal_id;
              return (
                <div className={`je-entry ${j.status === 'void' ? 'je-entry-void' : ''}`} key={j.journal_id}>
                  <div className="je-entry-row" onClick={() => setExpanded(isOpen ? null : j.journal_id)}>
                    {isOpen ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                    <span className="je-entry-num">{j.journal_number || `#${j.journal_id}`}</span>
                    <span className="je-entry-date">{(j.journal_date || '').slice(0, 10)}</span>
                    <span className="je-entry-memo">{j.memo || (j.source === 'bank_txn' ? 'Posted from bank txn' : '')}</span>
                    <span className="je-entry-total">{num(j.total_debit).toFixed(2)}</span>
                    <span className={`je-entry-status je-status-${j.status}`}>{j.status}</span>
                  </div>
                  {isOpen && (
                    <div className="je-entry-detail">
                      {jLines.map(l => {
                        const pcLabel = profitCenters.label(l.location_id, l.unit_id);
                        return (
                          <div className="je-detail-line" key={l.line_id}>
                            <span className="je-detail-acct">
                              {coaName(l.coa_id)}
                              {pcLabel && <span className="je-detail-pc">{pcLabel}</span>}
                            </span>
                            <span className="je-detail-amt">{num(l.debit) > 0 ? num(l.debit).toFixed(2) : ''}</span>
                            <span className="je-detail-amt">{num(l.credit) > 0 ? num(l.credit).toFixed(2) : ''}</span>
                          </div>
                        );
                      })}
                      {j.status === 'posted' && (
                        <div className="je-detail-actions">
                          <button className="je-btn je-btn-danger" type="button" onClick={() => handleVoid(j)} disabled={busy}>Void entry</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default JournalEntriesTab;
